/*
 * @Copyright 2023-2025. Institute for Future Intelligence, Inc.
 */

import React, { useMemo } from 'react';
import { useStore } from '../../stores/common';
import * as Selector from '../../stores/selector';
import { useLanguage } from '../../hooks';
import { Checkbox, ColorPicker } from 'antd';
import { LabelMark, MainMenuItem, MainSubMenu } from '../menuItem';
import { setWithUndo } from '../../undo/setWithUndo';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { useTranslation } from 'react-i18next';
import { usePrimitiveStore } from '../../stores/commonPrimitive';
import { screenshot, setMessage } from '../../helpers';
import {
  BIO_COLORING_LABELS,
  CHAMBER_COLORING_LABELS,
  CHAMBER_STYLE_LABELS,
  CHAMBER_VIEW_ANGLES,
  MATERIAL_LABELS,
  MolecularViewerColoring,
  MolecularViewerMaterial,
  MolecularViewerStyle,
  ViewAngle,
} from '../../view/displayOptions';
import { isCartoon } from '../../view/moleculeTools.ts';
import { ProjectType, SpaceshipDisplayMode } from '../../constants.ts';
import { ClickEvent, MenuItem, MenuRadioGroup, RadioChangeEvent } from '@szhsin/react-menu';
import i18n from '../../i18n/i18n.ts';

export const ViewAngleSubmenu = () => {
  const navigationView = useStore(Selector.navigationView);
  const setChanged = usePrimitiveStore(Selector.setChanged);
  const { t } = useTranslation();
  const lang = useLanguage();

  const setAngle = (viewAngle: ViewAngle) => {
    useStore.getState().set((state) => {
      const d = 3;
      switch (viewAngle) {
        case ViewAngle.PositiveXDirection: {
          if (navigationView) {
            state.projectState.navPosition = [state.projectState.molecularContainer.lx * d, 0, 0];
            state.projectState.navRotation = [0, 0, 0];
            state.projectState.navTarget = [0, 0, 0];
            state.projectState.navUp = [0, 0, 1];
          }
          state.projectState.cameraPosition = [state.projectState.molecularContainer.lx * d, 0, 0];
          state.projectState.cameraRotation = [0, 0, 0];
          state.projectState.cameraUp = [0, 0, 1];
          break;
        }
        case ViewAngle.NegativeXDirection: {
          if (navigationView) {
            state.projectState.navPosition = [-state.projectState.molecularContainer.lx * d, 0, 0];
            state.projectState.navRotation = [0, 0, 0];
            state.projectState.navTarget = [0, 0, 0];
            state.projectState.navUp = [0, 0, 1];
          }
          state.projectState.cameraPosition = [-state.projectState.molecularContainer.lx * d, 0, 0];
          state.projectState.cameraRotation = [0, 0, 0];
          state.projectState.cameraUp = [0, 0, 1];
          break;
        }
        case ViewAngle.PositiveYDirection: {
          if (navigationView) {
            state.projectState.navPosition = [0, state.projectState.molecularContainer.ly * d, 0];
            state.projectState.navRotation = [0, 0, 0];
            state.projectState.navTarget = [0, 0, 0];
            state.projectState.navUp = [0, 0, 1];
          }
          state.projectState.cameraPosition = [0, state.projectState.molecularContainer.ly * d, 0];
          state.projectState.cameraRotation = [0, 0, 0];
          state.projectState.cameraUp = [0, 0, 1];
          break;
        }
        case ViewAngle.NegativeYDirection: {
          if (navigationView) {
            state.projectState.navPosition = [0, -state.projectState.molecularContainer.ly * d, 0];
            state.projectState.navRotation = [0, 0, 0];
            state.projectState.navTarget = [0, 0, 0];
            state.projectState.navUp = [0, 0, 1];
          }
          state.projectState.cameraPosition = [0, -state.projectState.molecularContainer.ly * d, 0];
          state.projectState.cameraRotation = [0, 0, 0];
          state.projectState.cameraUp = [0, 0, 1];
          break;
        }
        case ViewAngle.PositiveZDirection: {
          if (navigationView) {
            state.projectState.navPosition = [0, 0, state.projectState.molecularContainer.lz * d];
            state.projectState.navRotation = [0, 0, 0];
            state.projectState.navTarget = [0, 0, 0];
            state.projectState.navUp = [1, 0, 0];
          }
          state.projectState.cameraPosition = [0, 0, state.projectState.molecularContainer.lz * d];
          state.projectState.cameraRotation = [0, 0, 0];
          state.projectState.cameraUp = [1, 0, 0];
          break;
        }
        case ViewAngle.NegativeZDirection: {
          if (navigationView) {
            state.projectState.navPosition = [0, 0, -state.projectState.molecularContainer.lz * d];
            state.projectState.navRotation = [0, 0, 0];
            state.projectState.navTarget = [0, 0, 0];
            state.projectState.navUp = [1, 0, 0];
          }
          state.projectState.cameraPosition = [0, 0, -state.projectState.molecularContainer.lz * d];
          state.projectState.cameraRotation = [0, 0, 0];
          state.projectState.cameraUp = [1, 0, 0];
          break;
        }
      }
    });
    setChanged(true);
  };

  return (
    <MainSubMenu hasPadding={true} label={i18n.t('molecularViewer.ViewDirection', lang)}>
      {CHAMBER_VIEW_ANGLES.map((item, idx) => {
        return (
          <MainMenuItem
            key={`${idx}-${item.value}`}
            onClick={() => {
              setAngle(item.value);
            }}
          >
            {t(item.label, lang)}
          </MainMenuItem>
        );
      })}
    </MainSubMenu>
  );
};

export const AutoRotateCheckBox = ({ isMac }: { isMac?: boolean }) => {
  const autoRotate = usePrimitiveStore(Selector.autoRotate);
  const { t } = useTranslation();
  const lang = useLanguage();

  const setAutoRotate = (checked: boolean) => {
    usePrimitiveStore.getState().set((state) => {
      state.autoRotate = checked;
      state.changed = true;
    });
  };

  return (
    <MainMenuItem stayAfterClick>
      <Checkbox
        style={{ width: '100%' }}
        checked={autoRotate}
        onChange={(e: CheckboxChangeEvent) => {
          const checked = e.target.checked;
          setWithUndo('Auto Rotate', !checked, checked, setAutoRotate);
        }}
      >
        {t('menu.view.AutoRotate', lang)}
        {isMac !== undefined && <LabelMark>({isMac ? '⌘' : 'Ctrl'}+M)</LabelMark>}
      </Checkbox>
    </MainMenuItem>
  );
};

export const Screenshot = () => {
  const loggable = useStore.getState().loggable;
  const logAction = useStore.getState().logAction;
  const { t } = useTranslation();
  const lang = useLanguage();

  const takeScreenshot = () => {
    screenshot('lab')
      .then(() => {
        if (loggable) logAction('Take Screenshot of Lab');
      })
      .catch((reason) => {
        setMessage('error', reason);
      });
  };

  return (
    <MainMenuItem hasPadding={true} onClick={takeScreenshot}>
      {t('molecularViewer.TakeScreenshot', lang)}
    </MainMenuItem>
  );
};

export const ExportModelMenuItem = () => {
  const { t } = useTranslation();
  const lang = useLanguage();

  return (
    <MainMenuItem
      hasPadding={true}
      onClick={() => {
        usePrimitiveStore.getState().set((state) => {
          state.showExportModelModal = true;
        });
      }}
    >
      {t('menu.project.Export3DPrintableModel', lang)}
    </MainMenuItem>
  );
};

export const AxesCheckBox = () => {
  const axes = useStore(Selector.chamberViewerAxes);
  const setChanged = usePrimitiveStore(Selector.setChanged);
  const { t } = useTranslation();
  const lang = useLanguage();

  const setAxes = (checked: boolean) => {
    useStore.getState().set((state) => {
      state.projectState.chamberViewerAxes = checked;
    });
    setChanged(true);
  };

  return (
    <MainMenuItem stayAfterClick>
      <Checkbox
        style={{ width: '100%' }}
        checked={axes}
        onChange={(e: CheckboxChangeEvent) => {
          const checked = e.target.checked;
          setWithUndo('Show Axes', !checked, checked, setAxes);
        }}
      >
        {t('molecularViewer.Axes', lang)}
      </Checkbox>
    </MainMenuItem>
  );
};

export const ContainerCheckBox = () => {
  const visible = useStore(Selector.molecularContainerVisible);
  const setChanged = usePrimitiveStore(Selector.setChanged);
  const { t } = useTranslation();
  const lang = useLanguage();

  const setVisible = (checked: boolean) => {
    useStore.getState().set((state) => {
      state.projectState.molecularContainerVisible = checked;
    });
    setChanged(true);
  };

  return (
    <MainMenuItem stayAfterClick>
      <Checkbox
        style={{ width: '100%' }}
        checked={visible}
        onChange={(e: CheckboxChangeEvent) => {
          const checked = e.target.checked;
          setWithUndo('Show Container', !checked, checked, setVisible);
        }}
      >
        {t('molecularViewer.Container', lang)}
      </Checkbox>
    </MainMenuItem>
  );
};

// Crystal lab only: the per-unit-cell wireframe. Stored inverted
// (chamberUnitCellsHidden) so existing projects, which lack the field, keep it.
export const UnitCellsCheckBox = () => {
  const hidden = useStore(Selector.chamberUnitCellsHidden);
  const setChanged = usePrimitiveStore(Selector.setChanged);
  const { t } = useTranslation();
  const lang = useLanguage();

  const setVisible = (checked: boolean) => {
    useStore.getState().set((state) => {
      state.projectState.chamberUnitCellsHidden = !checked;
    });
    setChanged(true);
  };

  return (
    <MainMenuItem stayAfterClick>
      <Checkbox
        style={{ width: '100%' }}
        checked={!hidden}
        onChange={(e: CheckboxChangeEvent) => {
          const checked = e.target.checked;
          setWithUndo('Show Unit Cells', !checked, checked, setVisible);
        }}
      >
        {t('molecularViewer.UnitCells', lang)}
      </Checkbox>
    </MainMenuItem>
  );
};

export const NavigationViewCheckBox = ({ isMac, popup }: { isMac?: boolean; popup?: boolean }) => {
  const navigationView = useStore(Selector.navigationView);
  const setChanged = usePrimitiveStore(Selector.setChanged);
  const { t } = useTranslation();
  const lang = useLanguage();

  const toggleNavigationView = (checked: boolean) => {
    useStore.getState().set((state) => {
      state.projectState.navigationView = checked;
      if (checked) {
        state.projectState.showInstructionPanel = false;
        state.projectState.spaceshipDisplayMode = SpaceshipDisplayMode.INSIDE_VIEW;
      } else {
        state.projectState.spaceshipDisplayMode = SpaceshipDisplayMode.NONE;
      }
    });
    usePrimitiveStore.getState().set((state) => {
      state.enableRotate = false;
    });
    setChanged(true);
  };

  return (
    <MainMenuItem stayAfterClick={false}>
      <Checkbox
        style={{ width: '100%' }}
        checked={navigationView}
        onChange={(e: CheckboxChangeEvent) => {
          const checked = e.target.checked;
          setWithUndo('Enable/Disable Navigation View', !checked, checked, toggleNavigationView);
        }}
      >
        {t('menu.view.NavigationView', lang)}
        <LabelMark>{popup ? '' : isMac ? '(⌘+U)' : '(Ctrl+U)'}</LabelMark>
      </Checkbox>
    </MainMenuItem>
  );
};

export const GalleryCheckBox = () => {
  const hideGallery = useStore(Selector.hideGallery);
  const setChanged = usePrimitiveStore(Selector.setChanged);
  const { t } = useTranslation();
  const lang = useLanguage();

  const showGallery = (checked: boolean) => {
    useStore.getState().set((state) => {
      state.projectState.hideGallery = !checked;
    });
    setChanged(true);
  };

  return (
    <MainMenuItem stayAfterClick={false}>
      <Checkbox
        style={{ width: '100%' }}
        checked={!hideGallery}
        onChange={(e: CheckboxChangeEvent) => {
          const checked = e.target.checked;
          setWithUndo('Show/Hide Gallery', !checked, checked, showGallery);
        }}
      >
        {t('menu.view.ShowGallery', lang)}
      </Checkbox>
    </MainMenuItem>
  );
};

export const FogCheckBox = () => {
  const foggy = useStore(Selector.chamberViewerFoggy);
  const setChanged = usePrimitiveStore(Selector.setChanged);
  const { t } = useTranslation();
  const lang = useLanguage();

  const setFoggy = (checked: boolean) => {
    useStore.getState().set((state) => {
      state.projectState.chamberViewerFoggy = checked;
    });
    setChanged(true);
  };

  return (
    <MainMenuItem stayAfterClick>
      <Checkbox
        style={{ width: '100%' }}
        checked={foggy}
        onChange={(e: CheckboxChangeEvent) => {
          const checked = e.target.checked;
          setWithUndo('Show Fog', !checked, checked, setFoggy);
        }}
      >
        {t('molecularViewer.Fog', lang)}
      </Checkbox>
    </MainMenuItem>
  );
};

export const BackgroundColor = () => {
  const color = useStore(Selector.chamberViewerBackground);
  const setChanged = usePrimitiveStore(Selector.setChanged);
  const { t } = useTranslation();
  const lang = useLanguage();

  const setColor = (color: string) => {
    useStore.getState().set((state) => {
      state.projectState.chamberViewerBackground = color;
    });
    setChanged(true);
  };

  return (
    <MainMenuItem hasPadding stayAfterClick>
      <ColorPicker
        trigger={'hover'}
        value={color}
        onChange={(value, hex) => {
          setWithUndo('Change Background Color', color, hex, setColor);
        }}
      >
        {t('molecularViewer.BackgroundColor', lang)}
      </ColorPicker>
    </MainMenuItem>
  );
};

export const GlobalStyleRadioSubmenu = ({ hasPadding }: { hasPadding?: boolean }) => {
  const testMolecules = useStore(Selector.testMolecules);
  const protein = useStore(Selector.protein);
  const molecularViewerStyle = useStore(Selector.chamberViewerStyle);
  const setChanged = usePrimitiveStore(Selector.setChanged);
  const { t } = useTranslation();
  const lang = useLanguage();

  const setStyle = (style: MolecularViewerStyle) => {
    useStore.getState().set((state) => {
      state.projectState.chamberViewerStyle = style;
      for (const m of state.projectState.testMolecules) {
        m.style = style;
      }
    });
    setChanged(true);
  };

  // Cartoon / Trace / Tube need a backbone with multiple residues to draw
  // anything. In molecular-modeling projects the chamber holds testMolecules;
  // in drug-discovery the chamber's protein lives at state.projectState.protein
  // (testMolecules is empty), so checking testMolecules alone hides those
  // styles even for proteins. Look at both.
  const multipleResidues = useMemo(() => {
    for (const m of testMolecules) {
      if (m.multipleResidues) return true;
    }
    if (protein) {
      const p = protein as { multipleResidues?: boolean; residues?: unknown[] };
      if (p.multipleResidues) return true;
      if (p.residues && p.residues.length > 1) return true;
    }
    return false;
  }, [testMolecules, protein]);

  const onItemClick = (e: ClickEvent) => {
    e.keepOpen = true;
  };

  return (
    <MainSubMenu hasPadding={hasPadding} label={i18n.t('molecularViewer.GlobalStyle', lang)}>
      <MenuRadioGroup
        value={molecularViewerStyle}
        onRadioChange={(e: RadioChangeEvent) => {
          // A click on the radio already selected fires too: no change, no undo step.
          if (e.value === molecularViewerStyle) return;
          setWithUndo('Select Molecular Viewer Style', molecularViewerStyle, e.value as MolecularViewerStyle, setStyle);
        }}
      >
        {CHAMBER_STYLE_LABELS.map((radio, idx) => {
          if (!multipleResidues && isCartoon(radio.value)) return null;
          return (
            <MenuItem type="radio" key={`${idx}-${radio.value}`} value={radio.value} onClick={onItemClick}>
              {t(radio.label, lang)}
            </MenuItem>
          );
        })}
      </MenuRadioGroup>
    </MainSubMenu>
  );
};

export const MaterialRadioSubmenu = ({ hasPadding }: { hasPadding?: boolean }) => {
  const molecularViewerMaterial = useStore(Selector.chamberViewerMaterial);
  const setChanged = usePrimitiveStore(Selector.setChanged);
  const { t } = useTranslation();
  const lang = useLanguage();

  const setMaterial = (material: MolecularViewerMaterial) => {
    useStore.getState().set((state) => {
      state.projectState.chamberViewerMaterial = material;
    });
    setChanged(true);
  };

  const onItemClick = (e: ClickEvent) => {
    e.keepOpen = true;
  };

  return (
    <MainSubMenu hasPadding={hasPadding} label={i18n.t('molecularViewer.Material', lang)}>
      <MenuRadioGroup
        value={molecularViewerMaterial}
        onRadioChange={(e: RadioChangeEvent) => {
          if (e.value === molecularViewerMaterial) return; // re-click: no change, no undo step
          setWithUndo(
            'Select Molecular Viewer Material',
            molecularViewerMaterial,
            e.value as MolecularViewerMaterial,
            setMaterial,
          );
        }}
      >
        {MATERIAL_LABELS.map((radio, idx) => (
          <MenuItem type="radio" key={`${idx}-${radio.value}`} value={radio.value} onClick={onItemClick}>
            {t(radio.label, lang)}
          </MenuItem>
        ))}
      </MenuRadioGroup>
    </MainSubMenu>
  );
};

export const ColoringRadioSubmenu = ({ hasPadding }: { hasPadding?: boolean }) => {
  const molecularViewerColoring = useStore(Selector.chamberViewerColoring);
  // The Protein Lab adds Conservation (computed from the UniProt family on first use).
  const projectType = useStore(Selector.projectType);
  const coloringLabels = projectType === ProjectType.PROTEIN_LAB ? BIO_COLORING_LABELS : CHAMBER_COLORING_LABELS;
  const setChanged = usePrimitiveStore(Selector.setChanged);
  const { t } = useTranslation();
  const lang = useLanguage();

  const setColoring = (coloring: MolecularViewerColoring) => {
    useStore.getState().set((state) => {
      state.projectState.chamberViewerColoring = coloring;
    });
    setChanged(true);
  };

  const onItemClick = (e: ClickEvent) => {
    e.keepOpen = true;
  };

  return (
    <MainSubMenu hasPadding={hasPadding} label={i18n.t('molecularViewer.Color', lang)}>
      <MenuRadioGroup
        value={molecularViewerColoring}
        onRadioChange={(e: RadioChangeEvent) => {
          if (e.value === molecularViewerColoring) return; // re-click: no change, no undo step
          setWithUndo(
            'Select Molecular Viewer Coloring',
            molecularViewerColoring,
            e.value as MolecularViewerColoring,
            setColoring,
          );
        }}
      >
        {coloringLabels.map((radio, idx) => (
          <MenuItem type="radio" key={`${idx}-${radio.value}`} value={radio.value} onClick={onItemClick}>
            <span
              title={
                radio.value === MolecularViewerColoring.Conservation
                  ? t('projectPanel.ConservationWhat', lang)
                  : undefined
              }
            >
              {t(radio.label, lang)}
            </span>
          </MenuItem>
        ))}
      </MenuRadioGroup>
    </MainSubMenu>
  );
};
